import { analyticsController } from "../../AppController";

/**
 * Centralised analytics helper for the Neo (Project Neo / AI clinical
 * assistant) surface. Every Neo event name is prefixed with ``Neo`` so the
 * events cluster together in Mixpanel and stay consistent across the page's
 * many sub-components (sidebar, conversation panel, agent panel, ticket board,
 * workflows, etc.).
 *
 * Keep the call sites thin: pass a short event suffix and any contextual
 * properties. Failures are swallowed so analytics can never break a clinical
 * workflow.
 */
export type NeoEventProps = Record<string, unknown>;

const EVENT_PREFIX = "Neo";

const buildEventName = (suffix: string): string => `${EVENT_PREFIX} ${suffix}`;

/**
 * Fire a Neo analytics event. ``suffix`` is appended to the ``Neo`` prefix,
 * e.g. ``trackNeoEvent("message sent")`` → ``"Neo message sent"``.
 */
export const trackNeoEvent = (suffix: string, props?: NeoEventProps): void => {
  try {
    analyticsController.logEvent(buildEventName(suffix), props);
  } catch {
    // Analytics must never interrupt the user flow.
  }
};

/**
 * Start a Mixpanel duration timer for a Neo event. Pair with a later
 * ``trackNeoEvent`` using the same suffix to record elapsed time (e.g. start
 * on "message sent", stop on "turn stream completed").
 */
export const timeNeoEvent = (suffix: string): void => {
  try {
    analyticsController.timeEvent(buildEventName(suffix));
  } catch {
    // no-op
  }
};
